import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";
import { getTeamUrl, tagUrl } from "src/react-query/endPoints";
import Token from "src/util/Token";
import { fetchParentTags } from "src/utils/api";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import { useDispatch } from "react-redux";
import arrowBack from "@iconify/icons-eva/arrow-ios-back-outline";
import { Icon } from "@iconify/react";

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  borderRadius: 8,
  padding: 12,
  fontFamily: "inherit",
  fontSize: 16,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: 8,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(["border-color", "background-color"], {
    duration: theme.transitions.duration.shorter,
  }),
  resize: "vertical",
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

export default function AddRFIDTagForm({ onBack, onSave, onCancel }) {
  const theme = useTheme();
  const brandGradient = theme.palette.gradients.brand;
  const brandHoverGradient = theme.palette.gradients.brandHover;
  const surfaceBorder = alpha(
    theme.palette.divider,
    theme.palette.mode === "dark" ? 0.6 : 0.2
  );
  const contrastText = theme.palette.primary.contrastText;
  const { teamId } = useSelectedTeam();
  const user = useAuth();
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;
  const [form, setForm] = useState({
    tag: "",
    balance: "",
    balance_amount: null,
    tagType: "standalone",
    expiry: null,
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [maxActiveType, setMaxActiveType] = useState("block");
  const [customLimit, setCustomLimit] = useState("");
  const [parentTags, setParentTags] = useState([]);
  const [loadingParentTags, setLoadingParentTags] = useState(false);
  const [selectedParentTag, setSelectedParentTag] = useState("");
  const [reserveAmount, setReserveAmount] = useState("");

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };
  const handleDateChange = (date) => {
    setForm({ ...form, expiry: date });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};
    if (!form.tag) newErrors.tag = "ID Tag is required";
    if (maxActiveType === "custom" && (!customLimit || customLimit < 1)) {
      newErrors.customLimit = "Please enter a valid custom limit";
    }
    // Add balance validation for all tag types - balance can be null but not 0
    if (
      form.balance_amount !== null &&
      form.balance_amount !== undefined &&
      form.balance_amount <= 0
    ) {
      newErrors.balance_amount =
        "Balance amount cannot be 0. It can be null or greater than 0";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      const payload = {
        tag: form.tag,
        parenttag: form.tagType === "child" ? selectedParentTag : null,
        expirydate: form.expiry
          ? form.expiry.toISOString().split("T")[0]
          : null,
        max_active_trans_count:
          form.tagType === "child"
            ? null
            : maxActiveType === "block"
            ? 0
            : maxActiveType === "unlimited"
            ? -1
            : Number(customLimit),
        note: form.notes,
        companyId: teamId,
        chargePointId: null,
        userId: user?.user?.id,
        balance_amount: form.balance_amount,
        tag_type: "RFID",
        reserve_amount: form.tagType === "parent" ? reserveAmount : null,
        is_parent_tag: form.tagType === "parent" ? 1 : 0,
      };

      try {
        const token = Token.get();
        const res = await fetch(getTeamUrl(teamId, tagUrl), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success === "true") {
          dispatch(
            setAlert({
              open: true,
              message: "RFID Tag added successfully!",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          if (onSave) onSave();
        } else {
          if (
            data.message &&
            data.message.includes("Duplicate entry") &&
            data.message.includes("idTag_UNIQUE")
          ) {
            setErrors({
              tag: "This ID Tag already exists. Please use a different tag ID.",
            });
          } else {
            setErrors({ api: data.message || "Failed to add tag." });
          }
        }
      } catch (err) {
        setErrors({ api: "Failed to add tag." });
      }
    }
  };
  const handleMaxActiveTypeChange = (e) => {
    setMaxActiveType(e.target.value);
    if (e.target.value !== "custom") setCustomLimit("");
  };
  const getMaxActiveHelperText = () => {
    switch (maxActiveType) {
      case "block":
        return "This tag will be blocked and cannot start any transactions.";
      case "unlimited":
        return "This tag can start unlimited concurrent transactions.";
      case "custom":
        return "Set the maximum number of concurrent transactions allowed for this tag.";
      default:
        return "";
    }
  };

  useEffect(() => {
    setLoadingParentTags(true);
    fetchParentTags(teamId)
      .then(setParentTags)
      .finally(() => setLoadingParentTags(false));
  }, [teamId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 4 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        transition: theme.transitions.create("background-color", {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      {/* Top Bar with Back Button and Title */}
      <Box sx={{ width: "100%", maxWidth: 1400, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            onClick={onBack}
            variant="contained"
            sx={{
              minWidth: 48,
              height: 40,
              mr: 2,
              px: 1,
              backgroundImage: brandGradient,
              color: contrastText,
              borderRadius: 2,
              '&:hover': {
                opacity: 1,
                backgroundImage: brandHoverGradient,
              },
            }}
          >
            <Icon icon={arrowBack} width={24} height={24} />
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Create new RFID Tag
          </Typography>
        </Stack>
      </Box>
      {/* Main Form Card */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        autoComplete="off"
        sx={{
          width: "100%",
          maxWidth: 1400,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2.5,
          boxShadow: theme.customShadows?.z8 ?? 2,
          border: `1px solid ${surfaceBorder}`,
          p: { xs: 2, md: 4 },
          transition: theme.transitions.create([
            "background-color",
            "box-shadow",
          ], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Grid container spacing={2}>
          {/* ID Tag */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              ID Tag <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              required
              fullWidth
              placeholder="Enter ID Tag"
              value={form.tag}
              onChange={handleChange("tag")}
              error={!!errors.tag}
              helperText={errors.tag}
              sx={{ mb: { xs: 2, md: 0 } }}
            />
          </Grid>
          {/* Balance Amount */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Balance Amount
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Enter Balance Amount"
                value={form.balance}
                onChange={handleChange("balance")}
                sx={{ mb: 0 }}
                disabled
                error={Boolean(errors.balance_amount)}
                helperText={errors.balance_amount}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
              <Button
                variant="contained"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  minWidth: 100,
                  height: 40,
                  backgroundImage: brandGradient,
                  color: contrastText,
                  '&:hover': { backgroundImage: brandHoverGradient },
                }}
                disabled
              >
                Topup
              </Button>
            </Stack>
            {/* Helpertext logic */}
            {(form.tagType === "parent" ||
              form.tagType === "child" ||
              form.tagType === "standalone") && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Balance can be added/updated after creating RFID Tag only.
              </Typography>
            )}
          </Grid>
          {form.tagType === "parent" && (
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Tag Reserve Amount
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter Reserve Amount"
                value={reserveAmount}
                onChange={(e) => setReserveAmount(e.target.value)}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                This amount will be reserved and not available for transactions.
              </Typography>
            </Grid>
          )}
          {/* Tag Type */}
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Tag Type</Typography>
            <FormControl component="fieldset" sx={{ width: "100%" }}>
              <RadioGroup
                row
                value={form.tagType}
                onChange={handleChange("tagType")}
                sx={{ gap: 3, mt: 1 }}
              >
                <FormControlLabel
                  value="standalone"
                  control={<Radio />}
                  label="Standalone"
                />
                <FormControlLabel
                  value="parent"
                  control={<Radio />}
                  label="Parent"
                />
                <FormControlLabel
                  value="child"
                  control={<Radio />}
                  label="Child"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Conditional fields for Tag Type = Child */}
          {form.tagType === "child" ? (
            <>
              {/* Parent ID Tag (left) */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Parent ID Tag <span style={{ color: "red" }}>*</span>
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    value={selectedParentTag}
                    onChange={(e) => setSelectedParentTag(e.target.value)}
                    displayEmpty
                    disabled={loadingParentTags}
                  >
                    <MenuItem value="" disabled>
                      {loadingParentTags ? "Loading..." : "Select Parent Tag"}
                    </MenuItem>
                    {parentTags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        {tag.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Max Active Transaction Count (right) */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Max. Active Transaction Count
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={maxActiveType}
                    onChange={handleMaxActiveTypeChange}
                    displayEmpty
                    disabled
                  >
                    <MenuItem value="block">Block all transactions</MenuItem>
                    <MenuItem value="unlimited">
                      Allow unlimited transactions
                    </MenuItem>
                    <MenuItem value="custom">Set custom limit</MenuItem>
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  This field is disabled for child tags. The maximum active
                  transaction count is managed by the parent tag.
                </Typography>
              </Grid>
              {/* Expiry Date on new row */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Expiry Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": { width: "100%" },
                      "& .MuiTextField-root": { width: "100%" },
                    }}
                  >
                    <DatePicker
                      value={form.expiry}
                      onChange={handleDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="MM/DD/YYYY"
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Max. Active Transaction Count
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={maxActiveType}
                    onChange={handleMaxActiveTypeChange}
                    displayEmpty
                  >
                    <MenuItem value="block">Block all transactions</MenuItem>
                    <MenuItem value="unlimited">
                      Allow unlimited transactions
                    </MenuItem>
                    <MenuItem value="custom">Set custom limit</MenuItem>
                  </Select>
                </FormControl>
                {(maxActiveType === "block" ||
                  maxActiveType === "unlimited") && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {getMaxActiveHelperText()}
                  </Typography>
                )}
                {maxActiveType === "custom" && (
                  <>
                    <TextField
                      type="number"
                      label="Custom Limit"
                      value={customLimit}
                      onChange={(e) => setCustomLimit(e.target.value)}
                      fullWidth
                      sx={{ mt: 2 }}
                      inputProps={{ min: 1 }}
                      error={!!errors.customLimit}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Set the maximum number of concurrent transactions allowed
                      for this tag.
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Expiry Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": { width: "100%" },
                      "& .MuiTextField-root": { width: "100%" },
                    }}
                  >
                    <DatePicker
                      value={form.expiry}
                      onChange={handleDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="MM/DD/YYYY"
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
            </>
          )}
          {/* Notes */}
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>MISC.</Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>Notes</Typography>
            <StyledTextarea
              minRows={4}
              value={form.notes}
              onChange={handleChange("notes")}
              placeholder="Enter any notes"
            />
          </Grid>
        </Grid>
        {/* Buttons */}
        <Stack direction="row" spacing={2} mt={4}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
              backgroundImage: brandGradient,
              color: contrastText,
              '&:hover': { backgroundImage: brandHoverGradient },
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
